import React, { useMemo, useState } from 'react'
import { graphql, Link } from 'gatsby'
import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'

import PostPreview from '../components/PostPreview'
import { useMatchMedia } from '../hooks/useMatchMedia'
import Pagination from '../components/slider/Pagination'
import { linkResolver } from '../utils/linkResolver'

const POSTS_MAX_COUNT = 3

const RelatedPostsSection = (props) => {
  const { slice } = props
  const { primary, items } = slice

  const { isMatching, calculated } = useMatchMedia('(max-width:768px)', true)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)

  const memoOptions = useMemo(
    () => ({
      initial: 0,
      loop: true,
      slideChanged: (slider) => {
        setCurrentSlide(slider.track.details.rel)
      },
      created: () => {
        setLoaded(true)
      },
      slides: {
        perView: 1,
        spacing: 20,
      },
    }),
    [],
  )

  const memoPosts = useMemo(() => {
    return items || slice.context?.latestPosts
  }, [])

  const [sliderRef, instanceRef] = useKeenSlider(memoOptions)

  const render = useMemo(() => {
    switch (true) {
      case isMatching && calculated:
        return (
          <>
            <div ref={sliderRef} className="keen-slider">
              <WithPosts className="keen-slider__slide" posts={memoPosts} />
            </div>
            <Pagination
              loaded={loaded}
              instanceRef={instanceRef}
              items={Array.from(Array(POSTS_MAX_COUNT).keys())}
              currentSlide={currentSlide}
            />
          </>
        )
      case !isMatching && calculated:
        return <WithPosts posts={memoPosts} />
      default:
        return <></>
    }
  }, [calculated, currentSlide, instanceRef, isMatching, loaded, sliderRef])

  return (
    <section
      className={`related-posts-section ${
        primary?.theme && `section--${primary?.theme}`
      }`}
    >
      <div className="container">
        <h3 className="title">{primary.title2.text}</h3>
        <div className="posts-box">{render}</div>
      </div>
    </section>
  )
}

export default RelatedPostsSection

export const query = graphql`
  fragment ArticlePageDataBody1RelatedPostsSection on PrismicArticlePageDataBody1RelatedPostsSection {
    id
    slice_type
    primary {
      title2 {
        text
      }
    }
    items {
      articles {
        document {
          ... on PrismicArticlePage {
            id
            uid
            type
            lang
            data {
              title {
                text
              }
              article_categories {
                article_category
              }
              short_description
              publish_date(formatString: "MMM DD,YYYY")
              invisible
              article_author {
                document {
                  ... on PrismicPerson {
                    id
                    data {
                      name
                      photo {
                        gatsbyImageData
                        alt
                      }
                    }
                  }
                }
              }
              image {
                gatsbyImageData
                alt
              }
              type
            }
          }
        }
      }
    }
  }
`

const WithPosts = ({ className, posts }) => {
  return (
    <>
      {posts
        ?.filter((node, i) => i < POSTS_MAX_COUNT)
        .map((node) => {
          const post = node.articles ? node.articles.document : node

          if (!post) {
            return null
          }

          return (
            <Link to={linkResolver(post)} key={node} className="post-item">
              <PostPreview data={{ data: post.data }} className={className} />
            </Link>
          )
        })}
    </>
  )
}
